import type { LicenseStatusResponse, PublicAdminSettings, WorldviewHistoryEntry, WorldviewSnapshot } from './types'
import { apiPath, appPath, navigateToAppPath } from './runtime'

// ── Authenticated fetch core ──────────────────────────────────────────────────

export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) { super(message); this.name = 'ApiError'; this.status = status }
}

export function getStoredToken(): string | null {
  return localStorage.getItem('godseye_token') || sessionStorage.getItem('godseye_token') || null
}

function clearSession() {
  ;['godseye_token','godseye_user'].forEach(k => {
    localStorage.removeItem(k); sessionStorage.removeItem(k)
  })
}

let _redirecting = false
export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getStoredToken()
  const headers = new Headers(options.headers as HeadersInit | undefined)
  if (!headers.has('Content-Type') && !(options.body instanceof FormData))
    headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const res = await fetch(resolveAppRequestUrl(url), { ...options, headers })
  if (res.status === 401 && !_redirecting) {
    _redirecting = true; clearSession()
    setTimeout(() => { navigateToAppPath('/login', true); _redirecting = false }, 150)
    throw new ApiError('Session expired — redirecting to login.', 401)
  }
  return res
}

function resolveAppRequestUrl(url: string): string {
  if (/^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(url)) return url
  if (url.startsWith('/api')) return apiPath(url.slice('/api'.length))
  if (url.startsWith('/')) return appPath(url)
  return url
}

async function _json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `Request failed (${res.status})`
    try { const j = await res.json(); msg = (j as { error?: string })?.error || msg } catch {}
    throw new ApiError(msg, res.status)
  }
  return res.json() as Promise<T>
}

export const apiGet    = <T>(url: string)                => apiFetch(url).then(r => _json<T>(r))
export const apiPost   = <T>(url: string, body?: unknown) => apiFetch(url, { method: 'POST',   body: body !== undefined ? JSON.stringify(body) : undefined }).then(r => _json<T>(r))
export const apiDelete = <T>(url: string)                => apiFetch(url, { method: 'DELETE' }).then(r => _json<T>(r))

// ── Worldview & history ───────────────────────────────────────────────────────

export async function fetchWorldviewSnapshot(): Promise<WorldviewSnapshot> {
  return apiGet<WorldviewSnapshot>('/api/worldview/snapshot')
}

export async function fetchPublicAdminSettings(): Promise<PublicAdminSettings> {
  // Public settings don't require auth — use plain fetch
  const res = await fetch(apiPath('/admin/settings/public'))
  if (!res.ok) throw new Error(`public settings request failed: ${res.status}`)
  return res.json()
}

export async function fetchWorldviewHistory(): Promise<WorldviewHistoryEntry[]> {
  return apiGet<WorldviewHistoryEntry[]>('/api/worldview/history')
}

export async function fetchWorldviewHistorySnapshot(filename: string): Promise<WorldviewSnapshot> {
  return apiGet<WorldviewSnapshot>(`/api/worldview/history/${encodeURIComponent(filename)}`)
}

export async function fetchLicenseStatus(): Promise<LicenseStatusResponse> {
  return apiGet<LicenseStatusResponse>('/api/license/status')
}

export async function activateLicense(licenseKey: string): Promise<LicenseStatusResponse> {
  return apiPost<LicenseStatusResponse>('/api/license/activate', { licenseKey })
}

export async function validateLicense(): Promise<LicenseStatusResponse> {
  return apiPost<LicenseStatusResponse>('/api/license/validate')
}
